import { SegmentType, Status } from '@essent/common';
import { unknownActionId } from '@core/ngrx';
import { getSettingsValue } from '@core/settings';
import { paymentDetailsFormId } from '@innogy/emobility-shared';
import {
  getCollectionObject,
  selectQueryParam,
  selectQueryParams,
} from '@innogy/utils-state';
import {
  type LeadFormProposition,
  MountType,
  ProductSelection,
  type SalesPartnerLeadData,
  type EmobilityLeadSegmentType,
} from '@integration/api-client/emobility';
import { createSelector } from '@ngrx/store';
import { getCustomerSegment } from '@customer/entity';

import { chargeCardTypeFormId } from './lead-form-charge-card-type.reducer';
import {
  customerDetailsFormId,
  CustomerType,
} from './lead-form-customer-details.reducer';
import { installationAddressFormId } from './lead-form.address.reducer';
import { leadConsentsFormId } from './lead-form.consents.reducer';
import {
  getPropositionFromQueryParams,
  getRelevantQueryParams,
} from './lead-form.queryparams';
import type { LeadFormQueryParams } from './lead-form.state';
import { emobilityLeadFormFeature } from './lead-form.state';
import { chargePointConfigurationFormId } from './lead-form-charge-point-configuration.reducer';

/* istanbul ignore next */
export const selectProspectState = createSelector(
  emobilityLeadFormFeature,
  (feature) => getCollectionObject(feature.prospect, unknownActionId)
);

/* istanbul ignore next */
export const selectProspect = createSelector(
  selectProspectState,
  (state) => state?.entry
);

/* istanbul ignore next */
export const selectProspectStatus = createSelector(
  selectProspectState,
  (state) => state?.status
);

export const selectProspectsHasError = createSelector(
  selectProspectStatus,
  (status) => status === Status.ERROR
);

/* istanbul ignore next */
export const selectProductState = createSelector(
  emobilityLeadFormFeature,
  (feature) => getCollectionObject(feature.products, unknownActionId)
);

/* istanbul ignore next */
export const selectProducts = createSelector(
  selectProductState,
  (state) => state?.entry?.results ?? []
);

/* istanbul ignore next */
export const selectProductStatus = createSelector(
  selectProductState,
  (state) => state?.status
);

export const selectProductsHasError = createSelector(
  selectProductStatus,
  (status) => status === Status.ERROR
);

export const selectChargePointOptions = createSelector(
  selectProducts,
  (products) => {
    return {
      totalOptions: products.length,
      colors: [...new Set(products.map((product) => product.productColor))],
      stationTypes: [
        ...new Set(products.map((product) => product.chargePointType)),
      ],
      cableOptions: [
        ...new Set(products.map((product) => product.cableIncluded)),
      ],
      mountTypes: Object.values(MountType),
    };
  }
);

export type ChargePointOptions = ReturnType<typeof selectChargePointOptions>;

export const selectChargePointConfigurationState = createSelector(
  emobilityLeadFormFeature,
  (feature) => feature['emobility-lead-charge-point-configuration']
);

export const selectChargePointConfigurationValue = createSelector(
  selectChargePointConfigurationState,
  (state) => state.progressiveForm.formState.value
);

/* istanbul ignore next */
export const selectProspectVerificationCode = createSelector(
  selectProspectState,
  (state) => state?.entry?.verificationCode
);

export const selectIsLoadingProspect = createSelector(
  selectProspectStatus,
  (status) => status == null || [Status.IDLE, Status.PENDING].includes(status)
);

export const selectIsLoadingProducts = createSelector(
  selectProductStatus,
  (status) => status == null || [Status.IDLE, Status.PENDING].includes(status)
);

export const selectShowProspectExpiredCodeError = createSelector(
  selectProspectState,
  (state) => state?.error?.status === 404
);

export const selectShowProspectGeneralError = createSelector(
  selectProspectState,
  (state) => state?.error?.status === 500
);

export const selectLeadFormCustomerDetails = createSelector(
  emobilityLeadFormFeature,
  (form) => form[customerDetailsFormId]
);
export const selectLeadFormCustomerDetailsFormState = createSelector(
  selectLeadFormCustomerDetails,
  (details) => details.progressiveForm.formState
);

export const selectLeadFormChargePointConfiguration = createSelector(
  emobilityLeadFormFeature,
  (form) => form[chargePointConfigurationFormId]
);

export const selectLeadFormChargePointConfigurationFormState = createSelector(
  selectLeadFormChargePointConfiguration,
  (state) => state.progressiveForm.formState
);

export const selectCustomerDetailsValue = createSelector(
  selectLeadFormCustomerDetailsFormState,
  (formState) => formState.value
);

export const selectLeadFormChargeCardType = createSelector(
  emobilityLeadFormFeature,
  (form) => form[chargeCardTypeFormId]
);

export const selectLeadFormChargeCardTypeFormState = createSelector(
  selectLeadFormChargeCardType,
  (chargeCardType) => chargeCardType.progressiveForm.formState
);

export const selectLeadFormChargeCardTypeValue = createSelector(
  selectLeadFormChargeCardTypeFormState,
  (formState) => formState.value
);

export const selectLeadFormAddress = createSelector(
  emobilityLeadFormFeature,
  (form) => form[installationAddressFormId]
);
export const selectLeadFormAddressState = createSelector(
  emobilityLeadFormFeature,
  (form) => form[installationAddressFormId]
);

export const selectLeadFormAddressFormState = createSelector(
  selectLeadFormAddressState,
  (state) => state.progressiveForm.formState
);

export const selectLeadFormAddressFormValue = createSelector(
  selectLeadFormAddressFormState,
  (formState) => formState.value
);
export const selectLeadFormInstallationAddress = createSelector(
  selectLeadFormAddressFormState,
  (form) => form.controls.installationAddressState
);
export const selectLeadFormCorrespondenceAddress = createSelector(
  selectLeadFormAddressFormState,
  (form) => form.controls.correspondenceAddressState
);

export const selectLeadFormPaymentDetailsState = createSelector(
  emobilityLeadFormFeature,
  (form) => form[paymentDetailsFormId]
);
export const selectLeadFormPaymentDetailsFormState = createSelector(
  selectLeadFormPaymentDetailsState,
  (state) => state.progressiveForm.formState
);

export const selectLeadFormPaymentDetailsValue = createSelector(
  selectLeadFormPaymentDetailsFormState,
  (paymentDetails) => paymentDetails.value
);

export const selectLeadFormConsentsRootState = createSelector(
  emobilityLeadFormFeature,
  (form) => form[leadConsentsFormId]
);
export const selectLeadFormConsents = createSelector(
  selectLeadFormConsentsRootState,
  (rootState) => rootState.progressiveForm.formState
);

export const selectLeadFormTrackingId = createSelector(
  selectLeadFormConsentsRootState,
  (rootState) => rootState.e2eTrackingId
);

export const selectLeadFormPaymentDetailsDisabled = createSelector(
  selectLeadFormConsentsRootState,
  (rootState) => rootState.paymentDetailsDisabled
);

export const selectLeadFormConsentsValue = createSelector(
  selectLeadFormConsents,
  (consents) => consents.value
);

export const selectLeadFormThankYouPage = createSelector(
  selectLeadFormConsentsRootState,
  (rootState) => rootState.thankYouPage
);

export const selectLeadFormRetryPage = createSelector(
  selectLeadFormConsentsRootState,
  (rootState) => rootState.retryPage
);

export const getPropositionQueryParams = createSelector(
  selectQueryParams,
  (params): LeadFormQueryParams | undefined => getRelevantQueryParams(params)
);

export const getProposition = createSelector(
  getPropositionQueryParams,
  selectLeadFormChargeCardTypeValue,
  (params, chargeCard): LeadFormProposition | undefined =>
    getPropositionFromQueryParams(params, chargeCard.chargeCardType)
);

export const getPropositionType = createSelector(
  getProposition,
  (proposition) => proposition?.proposition_id
);

export const isChargingStation = createSelector(
  getPropositionType,
  (proposition) => proposition === ProductSelection.CHARGING_STATION
);

export const selectIsLoginPromptEnabled = createSelector(
  selectLeadFormConsentsRootState,
  (rootState) => rootState.loginPromptEnabled
);

export const selectEmobilityCustomerSegment = createSelector(
  getCustomerSegment,
  (segment): EmobilityLeadSegmentType | undefined =>
    segment ? (segment as EmobilityLeadSegmentType) : undefined
);

/**
 * If the login prompt is enabled, the lead form only becomes enabled after
 * clicking a button which sets the 'nieuwe-klant' query parameter to true.
 */
export const selectLeadFormEnabled = createSelector(
  selectIsLoginPromptEnabled,
  selectQueryParam('nieuwe-klant'),
  (isLoginPromptEnabled, newCustomer) =>
    !isLoginPromptEnabled || newCustomer === 'ja'
);

/* istanbul ignore next */
export const selectCampaignId = createSelector(
  selectQueryParam('campaign-id'),
  (campaignId) => campaignId
);

/* istanbul ignore next */
export const selectExternalPartnerId = createSelector(
  selectQueryParam('external-partner-id'),
  (externalPartnerId) => externalPartnerId
);

/* istanbul ignore next */
export const selectLeadSegment = createSelector(
  selectQueryParam('lead-segment'),
  (leadSegment) => leadSegment
);

/* istanbul ignore next */
export const selectChannel = createSelector(
  selectQueryParam('channel'),
  (channel) => channel
);

/* istanbul ignore next */
export const selectProductType = createSelector(
  selectQueryParam('product-type'),
  (productType) => productType
);

/* istanbul ignore next */
export const selectServicePartner = createSelector(
  selectQueryParam('service-partner'),
  (servicePartner) => servicePartner
);

/* istanbul ignore next */
export const selectCampaign = createSelector(
  selectQueryParam('campaign'),
  (campaign) => campaign
);

/* istanbul ignore next */
export const selectEmployee = createSelector(
  selectQueryParam('employee'),
  (employee) => employee
);

/* istanbul ignore next */
export const selectStore = createSelector(
  selectQueryParam('store'),
  (store) => store
);

/* istanbul ignore next */
export const selectInvoiceGroup = createSelector(
  selectQueryParam('invoice-group'),
  (invoiceGroup) => invoiceGroup
);

export const selectSalesPartnerLeadData = createSelector(
  selectExternalPartnerId,
  selectLeadSegment,
  selectChannel,
  selectProductType,
  selectServicePartner,
  selectCampaign,
  selectEmployee,
  selectStore,
  selectInvoiceGroup,
  (
    externalPartnerId,
    leadSegment,
    channel,
    productType,
    servicePartner,
    campaign,
    employee,
    store,
    invoiceGroup
  ) => ({
    externalPartnerId,
    data: externalPartnerId
      ? ({
          lead_segment: leadSegment,
          channel,
          product_type: productType,
          service_partner: servicePartner,
          campaign,
          employee,
          store,
          invoice_group: invoiceGroup,
        } as SalesPartnerLeadData)
      : undefined,
  })
);

/* istanbul ignore next */
export const isEmobilityProductMailConsentEnabled = createSelector(
  getSettingsValue<boolean>('featureFlags', 'enableProductMailConsent'),
  (enabled) => enabled === true
);

/* istanbul ignore next */
export const isEmobilityIbanVerificationEnabled = createSelector(
  getSettingsValue<boolean>('featureFlags', 'enableEmobilityIbanVerification'),
  (enabled) => enabled === true
);

/* istanbul ignore next */
export const isEmobilityIbanInputDisabled = createSelector(
  getSettingsValue<boolean>('featureFlags', 'emobilityIbanInputDisabled'),
  (enabled) => enabled === true
);

export const selectIsAllowedSegment = createSelector(
  getCustomerSegment,
  (customerSegment) => {
    if (customerSegment === undefined) {
      return true;
    }

    return [SegmentType.HH, SegmentType.SOHO].some(
      (segment) => segment === customerSegment
    );
  }
);

export const selectIsBusinessCustomer = createSelector(
  selectCustomerDetailsValue,
  (customerDetails) =>
    [CustomerType.SME, CustomerType.SelfEmployed].includes(
      customerDetails.customerType as CustomerType
    )
);

export const showLeadForm = createSelector(
  selectProspectsHasError,
  selectProductsHasError,
  selectIsAllowedSegment,
  (prospectsHasError, productsHasError, isAllowedSegment) => {
    return isAllowedSegment && !prospectsHasError && !productsHasError;
  }
);
